import { useDraggable, useDroppable } from '@dnd-kit/core'
import { istLiegengeblieben, tagKurz } from '@netcon-work/shared'
import { GripVertical, Plus, Sun, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { useMarkieren } from '../../lib/markierung'
import { AufgabenVerweis } from '../AufgabeKontext'
import { MarkierKnoepfe } from '../Markierung'
import { SonnenKnopf } from './TelefonMarkieren'
import { Fristwort } from './Fristwort'
import type { Naechste } from './lage'

/**
 * **Der Vorlauf der Woche** – Bausteine des Kalenders für
 * [Z-20](../../../../../docs/phasen/zusatz-20-mein-tag-markieren.md).
 *
 * Nils, 05.10.2026: *„In der Kalenderansicht soll der Vorlauf für das Bestücken der «Mein
 * Tag»-Liste angezeigt werden, auch für Folgetage."* Oben fünf Spalten – heute und die nächsten
 * Werktage –, rechts die Rangfolge des Vorlaufs. Eine Zeile auf eine Spalte zu ziehen markiert sie
 * für diesen Tag. Ins Raster gezogen bleibt sie seit 13.10.2026 an der Uhrzeit stehen, an der man
 * loslässt; sonst legt der Plan bei jeder Änderung neu (Z-19).
 *
 * ## Ziehen mit dnd-kit, und nie nur mit der Maus
 *
 * Jede Zeile trägt zusätzlich die Knöpfe Heute · Morgen · Übermorgen, und dnd-kit bringt einen
 * Tastatursensor mit (Leitplanke 3). Auf dem Telefon ist das Ziehen aus: der Finger soll dort
 * scrollen, nicht greifen – markiert wird mit den Knöpfen.
 */

/** Ablageziele: eine Tagesspalte, oder das Raster des angezeigten Tages. */
export const ZIEL_RASTER = 'raster'
export const zielTag = (tag: string) => `tag:${tag}`
/**
 * **Woher ein Zug kommt** gehört in die Kennung (14.10.2026): dieselbe Aufgabe steht oft in zwei
 * Boxen zugleich – „Gefährdet" und „Zum Einplanen" –, und dnd-kit verlangt eindeutige Kennungen.
 * Mit zweimal derselben griff der Zug ins Leere.
 */
export const zugAufgabe = (taskId: string, herkunft: string) => `aufgabe:${herkunft}:${taskId}`
/**
 * **Ein Klotz im Raster** (13.10.2026) – eigene Kennung, weil dieselbe Aufgabe zugleich als Chip
 * in der Wochenleiste steht und eine geteilte mehrere Klötze hat. dnd-kit verlangt eindeutige.
 */
export const zugBlock = (taskId: string, nr: number) => `block:${nr}:${taskId}`

/** Aus einer Ablagekennung den Tag – `null`, wenn sie keiner ist. */
export function tagAusZiel(ziel: string | number | undefined, angezeigt: string): string | null {
  if (ziel === ZIEL_RASTER) return angezeigt
  return typeof ziel === 'string' && ziel.startsWith('tag:') ? ziel.slice(4) : null
}

/** Aus einer Zugkennung die Aufgabe. */
export function aufgabeAusZug(zug: string | number): string | null {
  if (typeof zug !== 'string') return null
  const treffer = /^(?:aufgabe|block):[^:]+:(.+)$/.exec(zug)
  return treffer ? treffer[1]! : null
}

const stunden = (h: number) =>
  h === 0.5 ? '30 min' : `${h.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`

/**
 * **Etwas, das man ziehen kann** – der Griff ist ein eigener Knopf, nicht die ganze Zeile.
 *
 * Läge der Zug auf der Zeile, finge er auch den Klick auf den Titel ab, und der öffnet die
 * Aufgabe (E-319). Am Griff dagegen ist klar, was passiert, und die Tastatur findet ihn.
 */
export function ZiehGriff({
  taskId,
  titel,
  herkunft,
  aus,
  children,
}: {
  taskId: string
  titel: string
  /** Welche Box – macht die Kennung eindeutig, siehe {@link zugAufgabe}. */
  herkunft: string
  /** Auf dem Telefon: kein Griff, kein Zug. */
  aus: boolean
  children: ReactNode
}) {
  /**
   * **Die Zeile selbst bleibt stehen**, gezogen wird ein Abbild (`DragOverlay` im Tagesplan,
   * 14.10.2026). Vorher wanderte die Zeile selbst – aus der seitlich scrollenden Wochenleiste
   * heraus wurde sie dabei abgeschnitten und war unterwegs unsichtbar.
   */
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: zugAufgabe(taskId, herkunft),
    data: { titel },
    disabled: aus,
  })
  return (
    <div
      ref={setNodeRef}
      className={cn('flex min-w-0 items-center gap-1', isDragging && 'opacity-40')}
    >
      {!aus && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          aria-label={`${titel} auf einen Tag ziehen`}
          title="Auf einen Tag ziehen – oder mit Leertaste greifen und mit den Pfeiltasten bewegen"
          className="shrink-0 cursor-grab touch-none rounded p-0.5 text-muted hover:bg-[#f3f2ef] hover:text-ink2 active:cursor-grabbing"
        >
          <GripVertical className="size-3.5" aria-hidden />
        </button>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

export type Tagesspalte = {
  tag: string
  /** „Heute", „Morgen", „Übermorgen", sonst der Wochentag. */
  name: string
  /** Was für diesen Tag markiert ist, mit dem gespeicherten Tag (für „seit Fr."). */
  aufgaben: {
    id: string
    title: string
    workspaceId: string
    am: string
    dauer: number
    /** Fest eingeplant um – „10:15", wenn sie im Raster an eine Uhrzeit gezogen wurde. */
    uhr?: string | undefined
  }[]
  /** Freie Stunden nach Terminen und Grundlast – dieselbe Rechnung wie der Kopf des Rasters. */
  frei: number
  /** Kein Arbeitstag oder ganz abwesend – die Spalte nimmt trotzdem an, sagt es aber. */
  frei_grund: string | null
}

/**
 * **Fünf Spalten, eine je Tag.** Der Balken vergleicht die markierte Dauer mit der freien Zeit –
 * rot, sobald mehr markiert ist, als der Tag hergibt. Nicht gesperrt: eine Absicht darf zu groß
 * sein, sie soll es nur sagen.
 */
export function Wochenleiste({
  spalten,
  angezeigt,
  anzeigen,
  heute,
  telefon,
  einplanen,
}: {
  spalten: readonly Tagesspalte[]
  angezeigt: string
  anzeigen: (tag: string) => void
  heute: string
  telefon: boolean
  /** „+ Einplanen" für diesen Tag – öffnet den Einplanen-Dialog bei ihm (Telefon). */
  einplanen?: (tag: string) => void
}) {
  return (
    <section
      className="min-w-0 rounded-card border border-line bg-surface shadow-task"
      aria-label="Vorlauf der Woche"
    >
      <h2 className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-2.5 text-[13.5px]">
        <span>
          Vorlauf der Woche{' '}
          <span className="text-[12px] font-normal text-muted max-sm:hidden">
            – was du dir für welchen Tag vorgenommen hast
          </span>
        </span>
        <span className="text-[11.5px] font-normal text-muted">
          {telefon
            ? 'Wischen für den nächsten Tag'
            : 'Am Griff ⋮⋮ auf einen Tag ziehen – oder ins Raster an eine Uhrzeit'}
        </span>
      </h2>
      {/**
       * **Auf dem Telefon eine Kachel je Tag, fast bildschirmbreit** (15.10.2026). Nils: *„die mein
       * tag kacheln sollten so sein das ich da mobil auch mit arbeiten kann also breiter (zum
       * swipen dann). mobil arbeitet man ja doch wohl am meisten."* 86 % Breite – der nächste Tag
       * lugt hervor und sagt, dass es weitergeht –, und jede Kachel rastet mittig ein.
       */}
      <div className="grid snap-x snap-mandatory grid-cols-5 gap-2.5 overflow-x-auto p-3 max-lg:grid-cols-[repeat(5,minmax(132px,1fr))] max-sm:grid-cols-[repeat(5,86%)] max-sm:gap-2.5 max-sm:px-3 max-sm:pb-3 max-sm:[scrollbar-width:none]">
        {spalten.map((s) => (
          <Spalte
            key={s.tag}
            spalte={s}
            gewaehlt={s.tag === angezeigt}
            anzeigen={anzeigen}
            heute={heute}
            telefon={telefon}
            {...(einplanen ? { einplanen } : {})}
          />
        ))}
      </div>
    </section>
  )
}

function Spalte({
  spalte: s,
  gewaehlt,
  anzeigen,
  heute,
  telefon,
  einplanen,
}: {
  spalte: Tagesspalte
  gewaehlt: boolean
  anzeigen: (tag: string) => void
  heute: string
  telefon: boolean
  einplanen?: (tag: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: zielTag(s.tag) })
  const markieren = useMarkieren()
  const summe = s.aufgaben.reduce((n, a) => n + a.dauer, 0)
  const voll = summe > s.frei + 1e-9 && summe > 0
  const anteil = s.frei > 0 ? Math.min(100, (summe / s.frei) * 100) : summe > 0 ? 100 : 0

  return (
    <div
      ref={setNodeRef}
      data-tagesspalte={s.tag}
      className={cn(
        'flex min-h-[150px] min-w-0 snap-start flex-col gap-1.5 rounded-[11px] border bg-[#fafaf8] px-2.5 pt-2 pb-2.5 transition-colors max-sm:snap-center max-sm:gap-2 max-sm:px-3 max-sm:pt-3',
        gewaehlt ? 'border-accent bg-accent-soft' : 'border-line',
        isOver && 'border-dashed border-sonne bg-sonne-bg',
      )}
    >
      <button
        type="button"
        onClick={() => anzeigen(s.tag)}
        aria-pressed={gewaehlt}
        className="flex cursor-pointer items-baseline justify-between gap-1.5 text-left"
        title={`${s.name} im Raster anzeigen`}
      >
        <b className="text-[13px] max-sm:text-[16px]">{s.name}</b>
        <span className="tabular text-[11px] text-muted max-sm:text-[13px]">
          {tagKurz(s.tag).split(', ')[1]}
        </span>
      </button>
      <div className="relative h-[5px] overflow-hidden rounded-full bg-line" aria-hidden>
        <i
          className={cn('absolute inset-y-0 left-0 rounded-full', voll ? 'bg-bad' : 'bg-sonne')}
          style={{ width: `${anteil}%` }}
        />
      </div>
      <p className={cn('tabular text-[11px]', voll ? 'font-semibold text-bad' : 'text-ink2')}>
        {s.frei_grund ?? `${stunden(summe)} von ${stunden(s.frei)} frei`}
        {voll && !s.frei_grund && ' · zu voll'}
      </p>
      {s.aufgaben.length === 0 && (
        <p className="text-[11.5px] text-muted max-sm:py-2 max-sm:text-[13px]">Nichts markiert</p>
      )}
      {/**
       * **Auf dem Telefon eine Arbeitsfläche, keine Vorschau** (15.10.2026): ganze Titel in großer
       * Schrift, ☀ verschiebt auf einen anderen Tag, ✕ nimmt heraus – dieselben Gesten wie im
       * Vorlauf. Gezogen wird dort nicht; der Finger wischt.
       */}
      {telefon &&
        s.aufgaben.map((a) => (
          <div
            key={a.id}
            className={cn(
              'flex min-w-0 items-center gap-2 rounded-[10px] border bg-surface py-1.5 pr-1.5 pl-3',
              istLiegengeblieben(a.am, heute) ? 'border-dashed border-sonne-line' : 'border-line',
            )}
          >
            <AufgabenVerweis
              aufgabeId={a.id}
              className="min-w-0 flex-1 py-1 text-left text-[15px] leading-snug"
            >
              {a.uhr && (
                <span className="tabular mr-1.5 text-[12.5px] font-semibold text-sonne">
                  {a.uhr}
                </span>
              )}
              {a.title}
            </AufgabenVerweis>
            <SonnenKnopf taskId={a.id} titel={a.title} />
            <button
              type="button"
              aria-label={`Markierung von ${a.title} entfernen`}
              onClick={() => markieren.mutate({ taskIds: [a.id], am: null })}
              className="tap grid size-10 shrink-0 cursor-pointer place-items-center rounded-[10px] text-muted hover:text-bad"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        ))}
      {telefon && einplanen && (
        <button
          type="button"
          onClick={() => einplanen(s.tag)}
          className="tap mt-auto flex cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-sonne-line bg-sonne-bg py-2.5 text-[14px] font-medium text-sonne"
        >
          <Plus className="size-4" aria-hidden /> Einplanen für {s.name}
        </button>
      )}
      {!telefon &&
        s.aufgaben.map((a) => (
          <div
            key={a.id}
            className={cn(
              'flex min-w-0 items-center gap-1 rounded-[7px] border bg-surface py-[3px] pr-1 pl-1.5 text-[12px]',
              istLiegengeblieben(a.am, heute) ? 'border-dashed border-sonne-line' : 'border-line',
            )}
          >
            <ZiehGriff taskId={a.id} titel={a.title} herkunft={`tag-${s.tag}`} aus={telefon}>
              <span className="flex min-w-0 items-center gap-1.5">
                {a.uhr && (
                  <span className="tabular shrink-0 text-[10.5px] font-semibold text-sonne">
                    {a.uhr}
                  </span>
                )}
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="min-w-0 flex-1 truncate text-left hover:underline"
                >
                  {a.title}
                </AufgabenVerweis>
              </span>
            </ZiehGriff>
            <button
              type="button"
              aria-label={`Markierung von ${a.title} entfernen`}
              title="Markierung entfernen"
              onClick={() => markieren.mutate({ taskIds: [a.id], am: null })}
              className="shrink-0 cursor-pointer rounded p-0.5 text-muted hover:text-bad"
            >
              <X className="size-3" aria-hidden />
            </button>
          </div>
        ))}
    </div>
  )
}

/**
 * **Zum Einplanen** – die Rangfolge des Vorlaufs ohne das, was schon markiert ist
 * (Nils: *„möglichst integriert und andernfalls in einer separaten Liste daneben"*).
 *
 * Dieselbe Reihenfolge wie im Vorlauf, damit man nicht zweimal überlegt, was als Nächstes dran
 * ist. Zwölf Zeilen, dann „weitere" – die ganze Rangfolge ist im Browser gerechnet.
 */
export function ZumEinplanen({
  zeilen,
  telefon,
  ziehbar = !telefon,
}: {
  zeilen: readonly Naechste[]
  telefon: boolean
  /** Ohne Raster daneben (My Work) gibt es nichts, wohin man ziehen könnte – dann kein Griff. */
  ziehbar?: boolean
}) {
  const [alle, setAlle] = useState(false)
  const sichtbar = alle ? zeilen : zeilen.slice(0, 12)
  return (
    <section className="rounded-card border border-line bg-surface shadow-task">
      <h2 className="flex items-center justify-between border-b border-line px-4 py-3 text-[13.5px]">
        <span>
          Zum Einplanen{' '}
          <span className="text-[12px] font-normal text-muted">– Rangfolge wie im Vorlauf</span>
        </span>
        <span className="text-[12px] font-normal text-muted">{zeilen.length} offen</span>
      </h2>
      {zeilen.length === 0 ? (
        <p className="px-4 py-5 text-center text-[12.5px] text-muted">
          Alles ist einem Tag zugeordnet.
        </p>
      ) : (
        <ul>
          {sichtbar.map((a, i) => (
            <li
              key={a.id}
              data-einplanen={a.id}
              className="border-b border-line px-3 py-1.5 last:border-0"
            >
              {/**
               * **Eine Zeile je Aufgabe** (05.10.2026, im Browser gesehen): drei Zeilen hoch
               * passten zwölf Aufgaben nicht auf einen Bildschirm, und die Wochenleiste lag beim
               * Ziehen außer Sicht. Die Weltmarke fällt weg – die Frist sagt hier mehr.
               */}
              <ZiehGriff taskId={a.id} titel={a.title} herkunft="einplanen" aus={!ziehbar}>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="tabular w-4 shrink-0 text-right text-[10.5px] text-muted">
                    {i + 1}
                  </span>
                  <AufgabenVerweis
                    aufgabeId={a.id}
                    className="min-w-0 flex-1 truncate text-left text-[13px] font-medium hover:underline"
                  >
                    {a.title}
                  </AufgabenVerweis>
                  <span className="shrink-0 text-[10.5px] text-muted max-sm:hidden">
                    <Fristwort
                      frist={a.frist}
                      zeitfenster={a.zeitfenster}
                      herkunft={a.fristHerkunft}
                      nurFrist
                    />
                  </span>
                  {/**
                   * **Auf dem Telefon ein ☀ statt dreier Knöpfe** (06.10.2026) – dieselbe Geste
                   * wie im Vorlauf; die drei Knöpfe ließen dem Titel auf 390 px kaum Platz.
                   */}
                  <span className="shrink-0">
                    {telefon ? (
                      <SonnenKnopf taskId={a.id} titel={a.title} />
                    ) : (
                      <MarkierKnoepfe taskIds={[a.id]} kompakt />
                    )}
                  </span>
                </div>
              </ZiehGriff>
            </li>
          ))}
        </ul>
      )}
      {zeilen.length > 12 && (
        <button
          type="button"
          onClick={() => setAlle((x) => !x)}
          className="w-full cursor-pointer border-t border-line px-4 py-2 text-left text-[11.5px] text-muted hover:text-accent-deep"
        >
          {alle ? '▴ wieder auf zwölf kürzen' : `▾ ${zeilen.length - 12} weitere anzeigen`}
        </button>
      )}
    </section>
  )
}

/**
 * **Markiert, passt an diesem Tag nicht** – die Absicht, für die der Tag zu klein ist.
 *
 * Getrennt von „Passt heute nicht mehr": das eine ist angefangene Arbeit, das andere ein Vorsatz.
 * Beide sichtbar, keiner still verschwunden. Die Knöpfe daneben schieben auf einen anderen Tag.
 */
export function MarkiertPasstNicht({
  aufgaben,
  dauer,
  telefon = false,
  ziehbar = true,
  hinweis,
}: {
  aufgaben: readonly { id: string; title: string }[]
  dauer: (id: string) => number
  telefon?: boolean
  /** Ohne Raster daneben (My Work) kein Griff. */
  ziehbar?: boolean
  /** Warum nichts passt, wenn es einen besonderen Grund gibt (z. B. nur Zeit für eine Welt). */
  hinweis?: string | null
}) {
  if (aufgaben.length === 0) return null
  return (
    <section className="rounded-card border border-sonne-line bg-surface shadow-task">
      <h2 className="flex items-center justify-between border-b border-sonne-line bg-sonne-bg px-4 py-3 text-[13.5px] text-sonne">
        <span className="flex items-center gap-1.5">
          <Sun size={13} aria-hidden /> Markiert, passt an diesem Tag nicht
        </span>
        <span className="text-[12px] font-normal">
          {aufgaben.length} · {stunden(aufgaben.reduce((s, a) => s + dauer(a.id), 0))}
        </span>
      </h2>
      {hinweis && <p className="border-b border-line px-4 py-2 text-[12px] text-ink2">{hinweis}</p>}
      <ul>
        {aufgaben.map((a) => (
          <li key={a.id} className="border-b border-line px-4 py-2 last:border-0">
            {telefon ? (
              <div className="flex items-center gap-2">
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="min-w-0 flex-1 text-left text-[14px] leading-snug font-medium"
                >
                  {a.title}
                  <span className="ml-1.5 text-[11.5px] font-normal text-muted">
                    {stunden(dauer(a.id))}
                  </span>
                </AufgabenVerweis>
                <SonnenKnopf taskId={a.id} titel={a.title} />
              </div>
            ) : (
              <ZiehGriff taskId={a.id} titel={a.title} herkunft="passtnicht" aus={!ziehbar}>
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="block truncate text-left text-[13px] font-medium hover:underline"
                >
                  {a.title}
                </AufgabenVerweis>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-[11.5px] text-muted">{stunden(dauer(a.id))}</span>
                  <MarkierKnoepfe taskIds={[a.id]} kompakt />
                </div>
              </ZiehGriff>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
